import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { TimelineExecution } from '../../pages/TimelinePage';
import {
  FiActivity, FiCheck, FiX, FiPause, FiClock,
  FiZoomIn, FiZoomOut, FiPlay, FiSkipBack,
  FiCalendar, FiFilter, FiLayers
} from 'react-icons/fi';

interface TimelineCanvasProps {
  executions: TimelineExecution[];
  selectedExecution: string | null;
  onSelectExecution: (id: string) => void;
  timeRange: { start: Date; end: Date };
  onTimeRangeChange: (range: { start: Date; end: Date }) => void;
  groupBy: 'workflow' | 'status' | 'none';
  autoScroll?: boolean;
}

// Time scale configuration
type TimeScale = 'minutes' | 'hours' | 'days' | 'weeks' | 'months';

interface TimeMarker {
  timestamp: number;
  label: string;
  major: boolean;
}

export const TimelineCanvas: React.FC<TimelineCanvasProps> = ({
  executions,
  selectedExecution,
  onSelectExecution,
  timeRange,
  onTimeRangeChange,
  groupBy,
  autoScroll = true
}) => {
  const { darkMode } = useTheme();
  const canvasRef = useRef<HTMLDivElement>(null);
  const rulerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });
  const [scale, setScale] = useState<TimeScale>('hours');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [showDensityMap, setShowDensityMap] = useState(true);
  // Reactive scroll state for density map viewport indicator
  const [scrollLeft, setScrollLeft] = useState(0);

  // Calculate pixels per millisecond based on scale and zoom
  const pixelsPerMs = useMemo(() => {
    const basePixels = {
      minutes: 0.5,
      hours: 0.008,
      days: 0.0003,
      weeks: 0.00004,
      months: 0.000005
    };
    return basePixels[scale] * zoomLevel;
  }, [scale, zoomLevel]);

  // Total width of the timeline in pixels
  const totalDuration = timeRange.end.getTime() - timeRange.start.getTime();
  const timelineWidth = totalDuration * pixelsPerMs;

  // Generate time markers based on current scale
  const timeMarkers = useMemo(() => {
    const markers: TimeMarker[] = [];
    const interval = {
      minutes: 5 * 60 * 1000,
      hours: 30 * 60 * 1000,
      days: 6 * 60 * 60 * 1000,
      weeks: 24 * 60 * 60 * 1000,
      months: 7 * 24 * 60 * 60 * 1000
    }[scale];

    let current = Math.ceil(timeRange.start.getTime() / interval) * interval;

    while (current <= timeRange.end.getTime()) {
      const date = new Date(current);
      let label = '';
      let major = false;

      switch (scale) {
        case 'minutes':
          label = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          major = date.getMinutes() === 0;
          break;
        case 'hours':
          label = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          major = date.getMinutes() === 0;
          break;
        case 'days':
          label = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
          major = date.getHours() === 0;
          if (date.getHours() === 0) {
            label += ' ' + date.toLocaleTimeString([], { hour: '2-digit' });
          }
          break;
        case 'weeks':
          label = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
          major = date.getDay() === 0;
          break;
        case 'months':
          label = date.toLocaleDateString([], { month: 'long', year: 'numeric' });
          major = true;
          break;
      }

      markers.push({ timestamp: current, label, major });
      current += interval;
    }

    return markers;
  }, [timeRange, scale]);

  // Group executions into lanes
  const lanes = useMemo(() => {
    if (groupBy === 'none') {
      return [{
        id: 'all',
        label: 'All Executions',
        executions: executions.sort((a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        ),
        color: getLaneColor(0, darkMode),
        stats: null
      }];
    }

    const groups = new Map<string, TimelineExecution[]>();

    executions.forEach(exec => {
      const key = groupBy === 'workflow' ? exec.workflow_id : exec.status;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(exec);
    });

    return Array.from(groups.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([id, execs], index) => {
        let stats = null;
        if (groupBy === 'workflow') {
          const active = execs.filter(e => e.status === 'active').length;
          const completed = execs.filter(e => e.status === 'completed').length;
          const failed = execs.filter(e => e.status === 'failed').length;
          const paused = execs.filter(e => e.status === 'paused').length;
          const totalDur = execs.reduce((sum, e) => sum + (e.duration_seconds || 0), 0);
          const avgDuration = totalDur / execs.length;

          stats = { active, completed, failed, paused, avgDuration };
        }

        return {
          id,
          label: groupBy === 'workflow' ? execs[0].workflow_name : id.charAt(0).toUpperCase() + id.slice(1),
          executions: execs.sort((a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          ),
          color: getLaneColor(index, darkMode),
          stats
        };
      });
  }, [executions, groupBy, darkMode]);

  // Calculate density map for mini heatmap
  const densityMap = useMemo(() => {
    const buckets = 100;
    const bucketSize = totalDuration / buckets;
    const densities = new Array(buckets).fill(0);

    executions.forEach(exec => {
      const start = new Date(exec.created_at).getTime();
      const bucketIndex = Math.floor((start - timeRange.start.getTime()) / bucketSize);
      if (bucketIndex >= 0 && bucketIndex < buckets) {
        densities[bucketIndex]++;
      }
    });

    const maxDensity = Math.max(...densities, 1);
    return densities.map(d => d / maxDensity);
  }, [executions, timeRange, totalDuration]);

  // Drag to scroll implementation
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!canvasRef.current) return;
    setIsDragging(true);
    setDragStart({
      x: e.pageX - canvasRef.current.offsetLeft,
      scrollLeft: canvasRef.current.scrollLeft
    });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !canvasRef.current) return;
    e.preventDefault();
    const x = e.pageX - canvasRef.current.offsetLeft;
    const walk = (x - dragStart.x) * 2;
    canvasRef.current.scrollLeft = dragStart.scrollLeft - walk;
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Reactive scroll handler — updates scrollLeft state so density map indicator stays in sync
  const handleCanvasScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollLeft(e.currentTarget.scrollLeft);
  }, []);

  // Calculate position for an execution
  const getExecutionPosition = (execution: TimelineExecution) => {
    const start = new Date(execution.created_at).getTime();
    const end = execution.end_time
      ? new Date(execution.end_time).getTime()
      : Math.min(currentTime, timeRange.end.getTime());

    const rawX = (start - timeRange.start.getTime()) * pixelsPerMs;
    const x = Math.max(0, rawX);

    const rawWidth = (end - start) * pixelsPerMs;
    const maxWidth = (timeRange.end.getTime() - timeRange.start.getTime()) * pixelsPerMs - x;
    const width = Math.min(Math.max(rawWidth, 4), maxWidth);

    return { x, width };
  };

  // Auto-scroll to "now" on initial load
  useEffect(() => {
    if (!autoScroll || !canvasRef.current) return;

    const now = Date.now();
    if (now >= timeRange.start.getTime() && now <= timeRange.end.getTime()) {
      const scrollPos = (now - timeRange.start.getTime()) * pixelsPerMs;
      canvasRef.current.scrollLeft = scrollPos - canvasRef.current.clientWidth / 2;
    }
  }, [autoScroll, timeRange, pixelsPerMs]);

  // Play/pause current time indicator
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying]);

  // Handle ruler scrubbing
  const handleRulerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!rulerRef.current) return;
    const rect = rulerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left + (canvasRef.current?.scrollLeft || 0);

    if (canvasRef.current) {
      const containerWidth = canvasRef.current.clientWidth;
      canvasRef.current.scrollLeft = clickX - containerWidth / 2;
    }
  };

  const handleDensityClick = (index: number) => {
    const bucketTime = timeRange.start.getTime() + (index / 100) * totalDuration;
    if (canvasRef.current) {
      const x = (bucketTime - timeRange.start.getTime()) * pixelsPerMs;
      canvasRef.current.scrollLeft = x - canvasRef.current.clientWidth / 2;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className={`
        flex items-center justify-between px-4 py-2 border-b
        ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
      `}>
        <div className="flex items-center gap-4">
          {/* Scale Selector */}
          <div className="flex items-center gap-2">
            <FiCalendar className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
            <select
              value={scale}
              onChange={(e) => setScale(e.target.value as TimeScale)}
              className={`
                px-3 py-1.5 rounded text-sm border
                ${darkMode
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'}
              `}
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </select>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.25))}
              className={`p-1.5 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <FiZoomOut size={18} />
            </button>
            <span className={`text-sm w-16 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              onClick={() => setZoomLevel(z => Math.min(3, z + 0.25))}
              className={`p-1.5 rounded ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <FiZoomIn size={18} />
            </button>
          </div>

          {/* Playback Controls */}
          <div className="flex items-center gap-1 border-l pl-4 ml-2 border-gray-600">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className={`
                p-2 rounded-full transition-colors
                ${isPlaying
                  ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                  : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}
              `}
            >
              {isPlaying ? <FiPause size={16} /> : <FiPlay size={16} />}
            </button>
            <button
              onClick={() => {
                const now = Date.now();
                if (canvasRef.current) {
                  canvasRef.current.scrollLeft = (now - timeRange.start.getTime()) * pixelsPerMs - canvasRef.current.clientWidth / 2;
                }
              }}
              className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              title="Jump to now"
            >
              <FiSkipBack size={16} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDensityMap(!showDensityMap)}
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded text-sm
              ${showDensityMap
                ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}
            `}
          >
            <FiLayers size={14} />
            Density
          </button>
        </div>
      </div>

      {/* Density Map Mini-Chart */}
      {showDensityMap && (
        <div className={`
          h-8 w-full border-b relative cursor-crosshair
          ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}
        `}>
          {densityMap.map((density, i) => (
            <div
              key={i}
              onClick={() => handleDensityClick(i)}
              className={`
                absolute top-0 bottom-0 hover:opacity-80 transition-opacity
                ${darkMode
                  ? density > 0.7 ? 'bg-blue-500' : density > 0.3 ? 'bg-blue-400/50' : 'bg-blue-300/30'
                  : density > 0.7 ? 'bg-blue-600' : density > 0.3 ? 'bg-blue-400' : 'bg-blue-200'}
              `}
              style={{
                left: `${i}%`,
                width: '1%',
                opacity: 0.3 + (density * 0.7)
              }}
              title={`${Math.round(density * 100)}% density`}
            />
          ))}
          {/* Viewport indicator — uses reactive scrollLeft state */}
          <div
            className="absolute top-0 bottom-0 border-x-2 border-red-500/50 bg-red-500/10 pointer-events-none"
            style={{
              left: `${(scrollLeft / timelineWidth) * 100}%`,
              width: `${((canvasRef.current?.clientWidth || 0) / timelineWidth) * 100}%`
            }}
          />
        </div>
      )}

      {/* Timeline Canvas */}
      <div
        ref={canvasRef}
        className={`
          flex-1 overflow-x-auto overflow-y-hidden relative cursor-grab
          ${isDragging ? 'cursor-grabbing' : ''}
          ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}
        `}
        onScroll={handleCanvasScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div style={{ width: `${timelineWidth + 224}px`, minHeight: '100%' }} className="relative">

          {/* Time Ruler */}
          <div
            ref={rulerRef}
            onClick={handleRulerClick}
            className={`
              sticky top-0 z-20 h-10 border-b flex items-end select-none
              ${darkMode ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'}
              backdrop-blur-sm
            `}
            style={{ width: `${timelineWidth}px` }}
          >
            {timeMarkers.map((marker, i) => (
              <div
                key={i}
                className="absolute bottom-0 flex flex-col items-center"
                style={{
                  left: `${(marker.timestamp - timeRange.start.getTime()) * pixelsPerMs}px`,
                  transform: 'translateX(-50%)'
                }}
              >
                <div className={`
                  w-px ${marker.major ? 'h-3' : 'h-2'}
                  ${darkMode ? 'bg-gray-500' : 'bg-gray-400'}
                `} />
                {marker.major && (
                  <span className={`
                    text-xs mt-1 whitespace-nowrap font-medium
                    ${darkMode ? 'text-gray-300' : 'text-gray-700'}
                  `}>
                    {marker.label}
                  </span>
                )}
              </div>
            ))}

            {/* Current Time Indicator in Ruler */}
            {currentTime >= timeRange.start.getTime() && currentTime <= timeRange.end.getTime() && (
              <div
                className="absolute top-0 bottom-0 w-px bg-red-500 z-30"
                style={{
                  left: `${(currentTime - timeRange.start.getTime()) * pixelsPerMs}px`,
                  boxShadow: '0 0 4px rgba(239, 68, 68, 0.5)'
                }}
              >
                <div className="absolute -top-1 -translate-x-1/2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded font-bold">
                  NOW
                </div>
              </div>
            )}
          </div>

          {/* Lanes */}
          <div className="relative pt-4 pb-8">
            {lanes.map((lane, laneIndex) => (
              <div
                key={lane.id}
                className={`
                  relative h-16 border-b group
                  ${darkMode ? 'border-gray-800 hover:bg-gray-800/30' : 'border-gray-200 hover:bg-white'}
                `}
              >
                {/* Lane Label - Sticky */}
                <div className={`
                  sticky left-0 z-10 w-56 h-full flex flex-col justify-center px-4 border-r
                  ${darkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-gray-50/95 border-gray-200'}
                  backdrop-blur-sm
                `}>
                  <div className="flex items-center mb-1">
                    <div
                      className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                      style={{ backgroundColor: lane.color }}
                    />
                    <div className={`font-medium text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {lane.label}
                    </div>
                  </div>

                  {lane.stats ? (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
                          {lane.executions.length} runs
                        </span>
                        <span className={darkMode ? 'text-gray-600' : 'text-gray-300'}>•</span>
                        <span className="text-blue-400">
                          {lane.stats.active} active
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {lane.stats.completed > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-700'}`}>
                            {lane.stats.completed} ✓
                          </span>
                        )}
                        {lane.stats.failed > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-700'}`}>
                            {lane.stats.failed} ✗
                          </span>
                        )}
                        {lane.stats.paused > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-yellow-900/50 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                            {lane.stats.paused} ⏸
                          </span>
                        )}
                      </div>
                      {lane.stats.avgDuration > 0 && (
                        <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Avg: {Math.round(lane.stats.avgDuration / 60)}m
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      {lane.executions.length} executions
                    </div>
                  )}
                </div>

                {/* Lane Content */}
                <div className="absolute top-0 left-56 right-0 h-full">
                  {/* Grid Lines */}
                  {timeMarkers.filter(m => m.major).map((marker, i) => (
                    <div
                      key={i}
                      className={`absolute top-0 bottom-0 w-px ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`}
                      style={{ left: `${(marker.timestamp - timeRange.start.getTime()) * pixelsPerMs}px` }}
                    />
                  ))}

                  {/* Current Time Line */}
                  {currentTime >= timeRange.start.getTime() && currentTime <= timeRange.end.getTime() && (
                    <div
                      className="absolute top-0 bottom-0 w-px bg-red-500/30 pointer-events-none"
                      style={{ left: `${(currentTime - timeRange.start.getTime()) * pixelsPerMs}px` }}
                    />
                  )}

                  {/* Executions — grouped by time slot to show concurrent runs */}
                  {(() => {
                    const timeSlots: { [key: string]: TimelineExecution[] } = {};
                    const slotSize = 10 * 60 * 1000;

                    lane.executions.forEach(exec => {
                      const start = new Date(exec.created_at).getTime();
                      const slotKey = Math.floor((start - timeRange.start.getTime()) / slotSize);
                      if (!timeSlots[slotKey]) timeSlots[slotKey] = [];
                      timeSlots[slotKey].push(exec);
                    });

                    return Object.entries(timeSlots).flatMap(([slotKey, slotExecutions]) => {
                      return slotExecutions.map((execution, execIdx) => {
                        const { x, width } = getExecutionPosition(execution);
                        const isSelected = selectedExecution === execution.id;
                        const isActive = execution.status === 'active';

                        const yOffset = execIdx * 12;
                        const height = 10;

                        return (
                          <div
                            key={execution.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectExecution(execution.id);
                            }}
                            className={`
                              absolute rounded cursor-pointer
                              transition-all duration-200 group/exec
                              ${isSelected
                                ? 'ring-2 ring-offset-1 ' + (darkMode ? 'ring-blue-400 ring-offset-gray-900' : 'ring-blue-500 ring-offset-white')
                                : ''
                              }
                              ${isActive ? 'animate-pulse' : ''}
                            `}
                            style={{
                              left: `${x}px`,
                              top: `${2 + yOffset}px`,
                              width: `${Math.max(width, 4)}px`,
                              height: `${height}px`,
                              maxWidth: '100%',
                              backgroundColor: getStatusColor(execution.status, darkMode),
                              boxShadow: isSelected ? `0 0 10px ${getStatusColor(execution.status, darkMode)}60` : 'none',
                              zIndex: isSelected ? 30 : 20 - execIdx,
                              opacity: execIdx > 0 ? 0.9 : 1
                            }}
                            title={`${execution.workflow_name} - ${execution.status} - ${new Date(execution.created_at).toLocaleTimeString()}`}
                          >
                            {/* Hover Card */}
                            <div className={`
                              absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 rounded-lg
                              min-w-[280px] max-w-[320px] z-50 pointer-events-none
                              opacity-0 group-hover/exec:opacity-100 transition-all duration-200
                              ${darkMode
                                ? 'bg-gray-800 text-white border border-gray-600 shadow-2xl'
                                : 'bg-white text-gray-900 border border-gray-200 shadow-2xl'
                              }
                            `}>
                              <div className="flex items-start justify-between mb-2 pb-2 border-b border-gray-600/30">
                                <div className="font-bold text-sm leading-tight pr-2">{execution.workflow_name}</div>
                                <span className={`
                                  text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold
                                  ${execution.status === 'active'
                                    ? 'bg-blue-500 text-white'
                                    : execution.status === 'completed'
                                      ? 'bg-green-500 text-white'
                                      : execution.status === 'failed'
                                        ? 'bg-red-500 text-white'
                                        : 'bg-yellow-500 text-white'
                                  }
                                `}>
                                  {execution.status}
                                </span>
                              </div>

                              <div className="mb-3">
                                <div className="flex justify-between text-xs mb-1 opacity-70">
                                  <span>Progress</span>
                                  <span>{execution.progress_percent}%</span>
                                </div>
                                <div className={`
                                  h-1.5 rounded-full overflow-hidden
                                  ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}
                                `}>
                                  <div
                                    className="h-full rounded-full bg-blue-500 transition-all"
                                    style={{ width: `${execution.progress_percent}%` }}
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Started:</div>
                                <div className="text-right font-mono">
                                  {new Date(execution.created_at).toLocaleTimeString()}
                                </div>

                                <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Duration:</div>
                                <div className="text-right font-mono">
                                  {execution.duration_seconds
                                    ? `${Math.round(execution.duration_seconds / 60)}m ${execution.duration_seconds % 60}s`
                                    : 'Running...'
                                  }
                                </div>

                                <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Phases:</div>
                                <div className="text-right">
                                  {execution.phases_completed}/{execution.phases_count} complete
                                </div>

                                <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Artifacts:</div>
                                <div className="text-right">
                                  {execution.artifacts_count} generated
                                </div>
                              </div>

                              {slotExecutions.length > 1 && (
                                <div className={`
                                  mt-2 pt-2 border-t text-xs flex items-center gap-2
                                  ${darkMode ? 'border-gray-600 text-yellow-400' : 'border-gray-200 text-yellow-600'}
                                `}>
                                  <FiLayers className="w-3 h-3" />
                                  <span>{slotExecutions.length} concurrent runs in this time slot</span>
                                </div>
                              )}

                              <div className={`
                                mt-2 pt-2 border-t text-[10px] uppercase tracking-wider text-center
                                ${darkMode ? 'border-gray-600 text-gray-500' : 'border-gray-200 text-gray-400'}
                              `}>
                                Click to view details
                              </div>
                            </div>
                          </div>
                        );
                      });
                    });
                  })()}
                </div>
              </div>
            ))}
          </div>

          {/* Hover Time Indicator */}
          {hoverTime && (
            <div
              className="absolute top-10 bottom-0 w-px bg-blue-500/50 pointer-events-none z-30"
              style={{ left: `${(hoverTime - timeRange.start.getTime()) * pixelsPerMs}px` }}
            >
              <div className={`
                absolute -top-6 -translate-x-1/2 px-2 py-0.5 rounded text-xs font-mono
                ${darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}
              `}>
                {new Date(hoverTime).toLocaleTimeString()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Time Cursor Tracking */}
      <div
        className="h-1 w-full relative overflow-hidden"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const percent = (e.clientX - rect.left) / rect.width;
          setHoverTime(timeRange.start.getTime() + percent * totalDuration);
        }}
        onMouseLeave={() => setHoverTime(null)}
      >
        <div className={`
          absolute top-0 bottom-0 w-full
          ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}
        `}>
          <div
            className="absolute top-0 bottom-0 w-1 bg-red-500 transition-all"
            style={{
              left: `${((currentTime - timeRange.start.getTime()) / totalDuration) * 100}%`,
              opacity: isPlaying ? 1 : 0.5
            }}
          />
        </div>
      </div>
    </div>
  );
};

// Helper functions
function getLaneColor(index: number, darkMode: boolean): string {
  const colors = darkMode
    ? ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']
    : ['#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED', '#DB2777', '#0891B2', '#65A30D'];
  return colors[index % colors.length];
}

function getStatusColor(status: string, darkMode: boolean): string {
  switch (status) {
    case 'active':
      return darkMode ? '#3B82F6' : '#2563EB';
    case 'completed':
      return darkMode ? '#10B981' : '#059669';
    case 'failed':
      return darkMode ? '#EF4444' : '#DC2626';
    case 'paused':
      return darkMode ? '#F59E0B' : '#D97706';
    default:
      return darkMode ? '#6B7280' : '#9CA3AF';
  }
}
